import gql from 'graphql-tag';

export const registrySearchArtifactMembershipsFragment = gql`
  fragment registrySearchArtifactMembershipsFragment on ArtifactCollectionMembership {
    ... on ArtifactCollectionMembership {
      id
      versionIndex
      createdAt
      aliases {
        id
        alias
      }
      artifact {
        id
        tags {
          id
          name
          tagCategoryName
          attributes
        }
        metadata
        size
        usedCount
      }
    }
  }
`;

export const registrySearchCollectionFragment = gql`
  fragment registrySearchCollectionFragment on ArtifactCollection {
    __typename
    ... on ArtifactPortfolio {
      id
      name
      description
      createdAt
      updatedAt
      defaultArtifactType {
        id
        name
      }
      project {
        id
        name
        entityName
        protectedArtifactAliases {
          alias
        }
      }
      tags {
        edges {
          node {
            id
            name
            tagCategoryName
            attributes
          }
        }
      }
    }
    __typename
    ... on ArtifactSequence {
      id
      name
      description
      createdAt
      updatedAt
      defaultArtifactType {
        id
        name
      }
      project {
        id
        name
        entityName
        protectedArtifactAliases {
          alias
        }
      }
      tags {
        edges {
          node {
            id
            name
            tagCategoryName
            attributes
          }
        }
      }
    }
  }
`;

export const registrySearchResultFragment = gql`
  fragment registrySearchResultFragment on ArtifactCollectionSearchConnection {
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
    edges {
      artifactMatch
      lastUpdatedAt
      cursor
      node {
        ...registrySearchCollectionFragment
      }
      artifactMemberships {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ...registrySearchArtifactMembershipsFragment
          }
        }
      }
    }
  }
  ${registrySearchCollectionFragment}
  ${registrySearchArtifactMembershipsFragment}
`;

// This query is used to fetch collections across ALL registries (in an org)
// for Registry global search from the registries homepage.
export const FETCH_ARTIFACT_COLLECTIONS_SEARCH_PARTIAL = gql`
  query FetchArtifactCollectionsPartial(
    $orgName: String!
    $searchTerm: String
    $cursor: String
    $limit: Int!
    $artifactsLimit: Int
  ) {
    organization(name: $orgName) {
      id
      orgEntity {
        id
        name
        artifactCollectionsSearch(
          searchTerm: $searchTerm
          cursor: $cursor
          limit: $limit
          artifactsLimit: $artifactsLimit
        ) {
          ...registrySearchResultFragment
        }
      }
    }
  }
  ${registrySearchResultFragment}
`;

export const FETCH_ARTIFACT_MEMBERSHIPS_PARTIAL = gql`
  query FetchArtifactMembershipsPartial(
    $collectionId: ID!
    $limit: Int!
    $after: String
    $searchTerm: String
  ) {
    artifactCollection(id: $collectionId) {
      id
      artifactMemberships(
        first: $limit
        after: $after
        searchTerm: $searchTerm
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ...registrySearchArtifactMembershipsFragment
          }
        }
      }
    }
  }
  ${registrySearchArtifactMembershipsFragment}
`;

// This query is used to fetch the usedByCount, project, and versionsCount for an artifact collection
// Eventually we should create endpoints for these values and add this to the search query,
// but for now we need this for the All Teams Model Registry page.
export const FETCH_ARTIFACT_COLLECTION_SUPPLEMENTAL = gql`
  query FetchArtifactCollectionSupplemental($collectionId: ID!) {
    artifactCollection(id: $collectionId) {
      id
      project {
        id
        name
      }
      artifacts {
        edges {
          node {
            id
            usedBy {
              edges {
                node {
                  id
                  user {
                    id
                    name
                  }
                }
              }
            }
          }
        }
        totalCount
      }
    }
  }
`;

export const registryCollectionFragment = gql`
  fragment RegistryCollectionFragment on ArtifactCollection {
    __typename
    id
    name
    description
    createdAt
    project {
      ...ProjectInfoFragment
    }
    type: defaultArtifactType {
      id
      name
    }
  }
`;

export const pageInfoFragment = gql`
  fragment PageInfoFragment on PageInfo {
    __typename
    endCursor
    hasNextPage
  }
`;
export const projectInfoFragment = gql`
  fragment ProjectInfoFragment on Project {
    id
    name
    entity {
      id
      name
    }
  }
`;

// This query is used to fetch simplified collection information for BasicSearch
export const FETCH_REGISTRY_COLLECTIONS = gql`
  query RegistryCollections(
    $organization: String!
    $registryFilter: JSONString
    $collectionFilter: JSONString
    $collectionTypes: [ArtifactCollectionType!] = [PORTFOLIO]
    $cursor: String
    $perPage: Int
  ) {
    organization(name: $organization) {
      id
      orgEntity {
        id
        name
        artifactCollections(
          projectFilters: $registryFilter
          filters: $collectionFilter
          collectionTypes: $collectionTypes
          after: $cursor
          first: $perPage
        ) {
          totalCount
          pageInfo {
            ...PageInfoFragment
          }
          edges {
            node {
              ...RegistryCollectionFragment
            }
          }
        }
      }
    }
  }

  ${registryCollectionFragment}
  ${pageInfoFragment}
  ${projectInfoFragment}
`;
